/**
 * 기기 정보 — 화면 상세 ▸ 기본 정보의 [기기 정보](2026-10-20). 기기를 알아보는 값·화면·망·상태·로그를 한 자리에.
 *
 * 처음에는 기기 상태(G10)를 화면 맨 아래 넓은 패널로 따로 두었는데, 기기를 알아보는 값(디바이스 ID·MAC·해상도)은 오른쪽
 * [기기 정보] 에 있어 같은 기기 이야기가 두 곳으로 갈렸다 — 망은 아래, MAC 은 위. 한 목록으로 합치고 사람이 기기를 볼 때의
 * 순서로 둔다: 무슨 기기인가(기기·디바이스 ID·등록) → 어떻게 비추나(해상도·방향·타임존) → 어디에 붙었나(망·IP·MAC) →
 * 지금 괜찮은가(CPU·메모리·온도·가동 시간) → 기기 로그.
 *
 * 상태 값의 출처(2026-10-19 기능 점검 G10).
 *
 * 예전에는 서버까지 오는 것이 저장공간·MAC·마지막 오류 한 줄뿐이라, 화면이 느리거나 끊길 때 CPU·메모리·온도·Wi-Fi 신호를
 * 보려면 현장에 가야 했고 로그는 adb 를 붙여야 했다. 이제 에이전트(0.7.17+)가 하트비트로 상태를 보내고, [기기 로그 받기] 를
 * 누르면 그 화면이 최근 로그를 올린다.
 *
 * **모르는 칸은 "—" 로 둔다.** 기기·OS 마다 읽을 수 있는 것이 다르다 — 안드로이드 8 이상은 앱이 기기 전체 CPU 를 읽지
 * 못하고, 윈도우는 온도를 관리자 권한으로만 읽는다.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { Download, FileText } from 'lucide-react';
import type { DeviceHealthView, DeviceLogEntry, Display, SignageOneClient } from '../api/client.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { HelpTip } from './HelpTip.js';
import { ErrorNote, formatBytes, relativeTime, RelTime } from './ui.js';

/** 가동 시간을 사람 말로 — "3일 4시간", "12분". */
export function formatUptime(seconds: number | undefined): string | null {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return null;
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}일 ${hours}시간`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${Math.max(0, minutes)}분`;
}

/** Wi-Fi 신호(dBm)를 강·중·약으로 — -60 이상 강, -70 이상 보통, 그 밖 약함(재생 끊김의 흔한 원인). */
export function signalLabel(rssi: number | null | undefined): { text: string; tone: 'ok' | 'warn' | 'bad' } | null {
  if (typeof rssi !== 'number') return null;
  if (rssi >= -60) return { text: `강함 (${rssi} dBm)`, tone: 'ok' };
  if (rssi >= -70) return { text: `보통 (${rssi} dBm)`, tone: 'warn' };
  return { text: `약함 (${rssi} dBm)`, tone: 'bad' };
}

export interface HealthFact {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'bad';
  hint?: string;
}

/** 그릴 줄들 — 모르는 칸은 "—". */
export function healthFacts(health: DeviceHealthView): HealthFact[] {
  const facts: HealthFact[] = [];
  facts.push({ label: '기기', value: [health.model, health.os].filter(Boolean).join(' · ') || '—' });
  facts.push({
    label: 'CPU',
    value: typeof health.cpu_percent === 'number' ? `${health.cpu_percent}%` : '—',
    tone: typeof health.cpu_percent === 'number' ? (health.cpu_percent >= 90 ? 'bad' : health.cpu_percent >= 75 ? 'warn' : 'ok') : undefined,
    hint: typeof health.cpu_percent === 'number' ? undefined : '안드로이드 8 이상은 앱이 기기 전체 CPU 를 읽지 못합니다',
  });
  if (typeof health.mem_total === 'number' && typeof health.mem_available === 'number' && health.mem_total > 0) {
    const freePercent = Math.round((health.mem_available / health.mem_total) * 100);
    facts.push({
      label: '메모리',
      value: `남은 ${formatBytes(health.mem_available)} / ${formatBytes(health.mem_total)} (${freePercent}%)`,
      tone: freePercent < 10 ? 'bad' : freePercent < 20 ? 'warn' : 'ok',
    });
  } else {
    facts.push({ label: '메모리', value: '—' });
  }
  const thermal = health.thermal_status && health.thermal_status !== 'none' ? ` · 발열 ${health.thermal_status}` : '';
  facts.push({
    label: '온도',
    value: typeof health.temperature_c === 'number' ? `${health.temperature_c}°C${thermal}` : thermal ? thermal.slice(3) : '—',
    tone:
      typeof health.temperature_c === 'number'
        ? health.temperature_c >= 80
          ? 'bad'
          : health.temperature_c >= 70
            ? 'warn'
            : 'ok'
        : undefined,
  });
  facts.push({
    label: '가동 시간',
    value:
      [formatUptime(health.uptime_sec) ? `기기 ${formatUptime(health.uptime_sec)}` : null, formatUptime(health.app_uptime_sec) ? `앱 ${formatUptime(health.app_uptime_sec)}` : null]
        .filter(Boolean)
        .join(' · ') || '—',
  });
  const network = health.network;
  if (network) {
    const signal = network.kind === 'wifi' ? signalLabel(network.wifi_rssi) : null;
    const band = typeof network.wifi_mhz === 'number' ? (network.wifi_mhz >= 5000 ? ' · 5GHz' : ' · 2.4GHz') : '';
    facts.push({
      label: '망',
      value: !network.connected
        ? '끊김'
        : network.kind === 'wifi'
          ? `Wi-Fi ${network.ssid ?? ''}${band}${signal ? ` · 신호 ${signal.text}` : ''}`
          : network.kind === 'ethernet'
            ? '유선'
            : network.kind,
      tone: !network.connected ? 'bad' : signal?.tone,
    });
    if (network.ipv4) facts.push({ label: 'IP', value: network.ipv4 });
  } else {
    facts.push({ label: '망', value: '—' });
  }
  return facts;
}

const TONE = { ok: 'text-emerald-700', warn: 'text-amber-700', bad: 'text-rose-700' } as const;

/** 로그 요청을 기다리는 동안 몇 초마다 다시 볼지, 얼마나 오래 볼지. */
const LOG_POLL_MS = 3_000;
const LOG_WAIT_MS = 2 * 60_000;

/** 상태 값 한 줄(`healthFacts`) — 모르는 칸의 까닭은 물음표로, 위험한 값은 색으로. */
function FactRow({ fact }: { fact: HealthFact | undefined }): ReactNode {
  if (!fact) return null;
  return (
    <>
      <dt>
        {fact.label}
        {fact.hint ? <HelpTip text={fact.hint} label={`${fact.label} 도움말`} /> : null}
      </dt>
      <dd className={fact.tone ? TONE[fact.tone] : undefined}>{fact.value}</dd>
    </>
  );
}

/** 상태 값을 목록에 놓는 차례 — 기기는 맨 위, 망·IP 는 어디에 붙었나 자리에, 나머지는 지금 괜찮은가 자리에. */
const STATE_FACTS = ['CPU', '메모리', '온도', '가동 시간'];
const FACT_ORDER = ['기기', '망', 'IP', ...STATE_FACTS];

/** 접힌 머리에 붙일 한 줄 — 빨강·노랑인 상태 값이 있으면 접혀 있어도 보이게(목록과 같은 차례로). */
export function deviceInfoSummary(health: DeviceHealthView | null | undefined): string {
  const worst = health
    ? healthFacts(health)
        .filter((fact) => fact.tone === 'bad' || fact.tone === 'warn')
        .sort((a, b) => FACT_ORDER.indexOf(a.label) - FACT_ORDER.indexOf(b.label))
    : [];
  if (worst.length > 0) return `주의 · ${worst.map((fact) => fact.label).join(' · ')}`;
  return '기기 · 해상도 · 망 · CPU · 메모리 · 온도 · 기기 로그';
}

export function DeviceInfo({
  client,
  display,
  canLogs,
}: {
  client: SignageOneClient;
  display: Pick<Display, 'id' | 'device_id' | 'timezone' | 'orientation' | 'rotation' | 'paired_at' | 'online' | 'health' | 'status'>;
  /** 기기 로그를 청하고 볼 수 있는가(`fleet:write`). */
  canLogs: boolean;
}): ReactNode {
  const displayId = display.id;
  const health = display.health;
  const action = useAction();
  const [waitingSince, setWaitingSince] = useState<number | null>(null);
  const logs = useAsync(() => (canLogs ? client.displayLogs(displayId) : Promise.resolve(null)), [client, displayId, canLogs]);
  const waiting = waitingSince !== null && Date.now() - waitingSince < LOG_WAIT_MS && Boolean(logs.data?.pending);
  const reload = logs.reload;
  const poll = useCallback(() => reload(), [reload]);
  usePolling(poll, LOG_POLL_MS, waiting);

  const request = (): void => {
    void action.run(
      () => client.requestDisplayLogs(displayId),
      (result) => {
        setWaitingSince(Date.now());
        logs.reload();
        void result;
      },
      (result) => (result.online ? '기기 로그를 청했습니다 — 화면이 곧 올립니다' : '기기 로그를 청했습니다 — 화면이 꺼져 있어 30분 안에 다시 붙으면 올립니다'),
    );
  };

  const facts = new Map((health ? healthFacts(health) : []).map((fact) => [fact.label, fact]));
  const items: DeviceLogEntry[] = logs.data?.items ?? [];
  return (
    <div className="flex flex-col gap-3">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      <dl className="kv">
        {/* 무슨 기기인가 */}
        <FactRow fact={facts.get('기기')} />
        <dt>디바이스 ID</dt>
        <dd className="mono">{display.device_id}</dd>
        <dt>등록</dt>
        <dd>
          <RelTime iso={display.paired_at} />
        </dd>

        {/* 어떻게 비추나 */}
        <dt>해상도</dt>
        <dd>{display.status.screen_w && display.status.screen_h ? `${display.status.screen_w} × ${display.status.screen_h}` : '—'}</dd>
        <dt>방향</dt>
        <dd>
          {display.orientation === 'portrait' ? '세로' : '가로'}
          {display.rotation ? ` · ${display.rotation}°` : ''}
        </dd>
        <dt>타임존</dt>
        <dd>{display.timezone ?? '(스케줄 기준)'}</dd>

        {/* 어디에 붙었나 */}
        <FactRow fact={facts.get('망')} />
        <FactRow fact={facts.get('IP')} />
        {/*
          MAC — 공유기 DHCP 예약·방화벽 허용을 잡을 때 묻는 값. 에이전트가 하트비트로 보고한다(0.7.15~).
          모르면 왜 모르는지 한 줄 — 안드로이드 11+ 는 OS 가 앱에 주지 않아 기기 설정에서 봐야 한다.
        */}
        <dt>MAC</dt>
        <dd>
          {display.status.mac ? (
            <span className="mono">{display.status.mac}</span>
          ) : (
            <span className="block">
              —
              <span className="mt-0.5 block text-[11.5px] text-slate-400">
                보고 없음 · 안드로이드 11 이상은 OS 가 앱에 주지 않습니다(기기 설정 › 네트워크에서 확인)
              </span>
            </span>
          )}
        </dd>

        {/* 지금 괜찮은가 */}
        {STATE_FACTS.map((label) => (
          <FactRow key={label} fact={facts.get(label)} />
        ))}
      </dl>
      <p className="hint">
        {health
          ? `기기 상태는 ${relativeTime(health.at)} 보고${display.online ? '' : ' · 화면이 꺼져 있어 마지막으로 받은 값입니다'}`
          : '기기 상태(CPU·메모리·온도·망)는 아직 보고받지 못했습니다 — 에이전트 0.7.17 부터 보냅니다.'}
      </p>
      {canLogs ? (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium text-slate-700">기기 로그</span>
            <button type="button" className="btn btn-tiny" disabled={action.pending || waiting} onClick={request}>
              <FileText className="h-3.5 w-3.5" strokeWidth={2} />
              {waiting ? '기다리는 중…' : '받기'}
            </button>
          </div>
          {logs.data?.pending ? (
            <p className="hint mt-1">요청 {relativeTime(logs.data.pending.requested_at)} — 화면이 받기를 기다립니다</p>
          ) : null}
          {items.length > 0 ? (
            <ul className="mt-1 space-y-1 text-[13px]">
              {items.slice(0, 5).map((item) => (
                <li key={item.id} className="flex items-center gap-2">
                  <a className="link inline-flex items-center gap-1" href={client.displayLogUrl(displayId, item.id)} download>
                    <Download className="h-3.5 w-3.5" strokeWidth={2} />
                    {relativeTime(item.received_at)}
                  </a>
                  <span className="cell-muted">
                    {formatBytes(item.size)}
                    {item.app_version ? ` · ${item.app_version}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
